// Centralized configuration for all blog posts
// This makes the blog listing dynamic and easy to maintain
//
// `author` is the free-text display string; `authors` is the machine-readable list the
// BibTeX generator uses (see citation.js). Keep them in sync - the array is separate
// because the five display strings below already use four different separator styles
// ("A, B, and C" / "A and B" / "A, B, C" / "A, B and C"), and a parser that guessed wrong
// would emit a wrong author list into someone's paper.
// `paperArxivIds` links a post to its companion paper(s); the citation panel pulls their
// metadata from src/publications/publications.json by `arxiv_id`, so nothing is duplicated
// here.

export const blogPosts = [
  {
    id: 'statutory-construction-ai',
    title: 'Statutory Construction and Interpretation for AI',
    date: '2025-08-15',
    author: 'Nimra Nadeem, Lucy He, Michel Liao, and Peter Henderson',
    authors: ['Nimra Nadeem', 'Lucy He', 'Michel Liao', 'Peter Henderson'],
    paperArxivIds: ['2509.01186'],
    excerpt: 'How interpretive ambiguity in Constitutional AI systems can lead to unpredictable behavior, and how legal theory can help constrain this ambiguity.',
    route: '/blog/statutory-construction-ai',
    hidden: false
  },
  {
    id: 'ai-federalism-preemption',
    title: 'AI "Born Secret"? The Atomic Energy Act, AI, and Federalism',
    date: '2025-09-10',
    author: 'Kylie Zhang and Peter Henderson',
    authors: ['Kylie Zhang', 'Peter Henderson'],
    paperArxivIds: [],
    excerpt: 'Examining how existing federal laws, particularly the Atomic Energy Act of 1954, may preempt state-level AI regulations covering nuclear and radiological harms.',
    route: '/blog/ai-federalism-preemption',
    hidden: false
  },
  {
    id: 'cybersecurity-risk-bubble',
    title: 'The "Bubble" of Risk: Improving Assessments for Offensive Cybersecurity Agents',
    date: '2025-07-15',
    author: 'Boyi Wei, Benedikt Stroebl, Jiacen Xu, Joie Zhang, Zhou Li, Peter Henderson',
    authors: ['Boyi Wei', 'Benedikt Stroebl', 'Jiacen Xu', 'Joie Zhang', 'Zhou Li', 'Peter Henderson'],
    paperArxivIds: ['2505.18384'],
    excerpt: 'How adversaries can adapt and modify AI models to expand risk far beyond static safety assessments, with implications for cybersecurity and regulatory compliance.',
    route: '/blog/cybersecurity-risk-bubble'
  },
  {
    id: 'biorisk-eval',
    title: 'The Limits of Data Filtering in Bio-Foundation Models',
    date: '2025-11-22',
    author: 'Boyi Wei, Matthew Siegel, Peter Henderson',
    authors: ['Boyi Wei', 'Matthew Siegel', 'Peter Henderson'],
    paperArxivIds: ['2510.27629'],
    excerpt: 'Exploring the best practises for biorisk assessments on open-weight bio-foundation models, and assessing the limits of data filtering as a means to address safety concerns.',
    route: '/blog/biorisk-eval',
    hidden: false
  },
  {
    id: 'public-defender-ai',
    title: 'How Can AI Strengthen Public Defense?',
    date: '2026-07-08',
    author: 'Dominik Stammbach, Patty Liu and Peter Henderson',
    authors: ['Dominik Stammbach', 'Patty Liu', 'Peter Henderson'],
    paperArxivIds: ['2510.22933', '2601.14348'],
    excerpt: 'We investigate how AI can support public defense. This post summarizes our findings from structured qualitative interviews and a collaboration with the NJ Office of the Public Defender.',
    route: '/blog/public-defender-ai',
    hidden: false
  },
  {
    id: 'oral-args-eval',
    title: 'Facts and Fictions: Is AI-Assisted Oral Argument Preparation Worth the Hype?',
    date: '2026-06-24',
    author: 'Kylie Zhang, Nimra Nadeem, Lucia Zheng, Dominik Stammbach, Peter Henderson',
    authors: ['Kylie Zhang', 'Nimra Nadeem', 'Lucia Zheng', 'Dominik Stammbach', 'Peter Henderson'],
    paperArxivIds: ['2603.04718'],
    excerpt: 'Introducing a tiered framework to evaluate how good frontier models are at helping attorneys prepare for oral arguments.',
    route: '/blog/oral-args-eval',
    hidden: false
  },
  {
    id: 'simulating-people',
    title: 'Better Simulations of People for Teaching People to Be Better Strategists',
    date: '2026-03-18',
    author: 'Peter Henderson',
    authors: ['Peter Henderson'],
    paperArxivIds: ['2603.04718', '2606.17657'],
    excerpt: 'Two new papers tackle improving and evaluating AI simulations of human decision-making, pointing toward a future where AI helps us become better advocates, policymakers, and leaders.',
    route: '/blog/simulating-people',
    hidden: false
  }
];

// Helper function to get blog posts sorted by date (newest first)
export const getSortedBlogPosts = () => {
  return [...blogPosts]
    .filter(post => !post.hidden)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Helper function to get a specific blog post by ID
export const getBlogPostById = (id) => {
  return blogPosts.find(post => post.id === id);
};

// Helper function to format date for display
export const formatDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}; 